import classNames from 'clsx';
import { isUndefined } from 'lodash-es';
import {
  type FocusEvent,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  useRef,
  useState,
  type WheelEvent,
} from 'react';

import {
  KeyboardControls,
  LARGE_STEP_FACTOR,
  MIN_ZOOM_STEP,
  SMALL_STEP_FACTOR,
  ZOOM_FACTOR,
} from './constants.js';
import { chartEventsWrapper } from './InteractionsOverlay.sty.js';
import type {
  InteractionMode,
  InteractionsOverlayProps,
  InteractionTriggers,
} from './types.js';
import { capSelectionAtBoundaries } from './utils/cap-selection-at-boundaries.js';
import { centerPositionVertically } from './utils/center-position-vertically.js';
import { getActiveMode } from './utils/get-active-mode.js';
import { isOwnEvent } from './utils/is-own-event.js';
import { normalizeSelectionArea } from './utils/normalize-selection-area.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import { usePointerEvents } from '../../hooks/usePointerEvents.js';
import { usePreventScrollOnWheel } from '../../hooks/usePreventScrollOnWheel.js';
import type {
  DragEndProps,
  DragMoveProps,
  DragStartProps,
  PinchMoveProps,
  PointerEventModifiers,
  PointerEventPosition,
  RelativeAbsolutePosition,
  SwipeMoveProps,
} from '../../types/pointer-events.js';
import { calculateMousePositions } from '../../utils/calculate-mouse-positions.js';

const isTemporaryZoomMode = (event: WheelEvent | KeyboardEvent) =>
  event.metaKey || event.ctrlKey;

const isTemporaryPanMode = (event: WheelEvent | KeyboardEvent) => event.altKey;

/**
 * @internal
 * @param props -
 */
export const InteractionsOverlay = forwardRef<
  HTMLDivElement,
  PropsWithChildren<InteractionsOverlayProps>
>((props, forwardedRef) => {
  const {
    style,
    className,
    mode,
    pinned,
    selectionArea,
    width,
    height,
    isZoomDisabled,
    isZoomXDisabled,
    onModeChange,
    onFocus,
    onHover,
    onLeave,
    onPanStart,
    onPanChange,
    onPanEnd,
    onTogglePin,
    onReset,
    onSelectStart,
    onSelectChange,
    onSelectEnd,
    onZoomStart,
    onZoomChange,
    onZoomEnd,
    children,
    ...restProps
  } = props;

  //#region Internal state
  /**
   * Keeps track of full pan delta interaction in pixels
   */
  const panDeltaRef = useRef<number>(undefined);
  /**
   * Keeps track of mouse position
   */
  const lastMousePositionRef = useRef<RelativeAbsolutePosition>(undefined);

  const eventCancelled = useRef(false);
  //#endregion

  const [cursorMode, setCursorMode] = useState<InteractionMode | 'zoomOut'>(
    mode,
  );

  const htmlRef = useRef<HTMLDivElement>(null);

  usePreventScrollOnWheel(htmlRef);

  const rootRef = useMergeRefs([htmlRef, forwardedRef]);

  /**
   * Resets internal state
   */
  const reset = () => {
    panDeltaRef.current = undefined;
    lastMousePositionRef.current = undefined;
  };

  //#region useChartEvents callbacks
  const onDragStart = (event: DragStartProps & PointerEventModifiers) => {
    if (getActiveMode(mode, event) === 'pan') {
      onPanStart?.({ triggeredBy: 'pointer' });
      panDeltaRef.current = 0;
    } else {
      onSelectStart?.({
        triggeredBy: 'pointer',
        position: event.startHoldingAt,
      });
    }
  };

  const onDragMove = (event: DragMoveProps & PointerEventModifiers) => {
    const {
      startHoldingAt: startHoldingAtProp,
      currentPosition: currentPositionProp,
    } = event;

    const startHoldingAt = centerPositionVertically(startHoldingAtProp, height);
    const currentPosition = centerPositionVertically(
      currentPositionProp,
      height,
    );

    const activeMode = getActiveMode(mode, event, isZoomXDisabled);

    if (activeMode === 'pan') {
      if (!isUndefined(panDeltaRef.current)) {
        const fullInteractionDelta =
          currentPosition.relative.x - startHoldingAt.relative.x;

        const currentDelta = fullInteractionDelta - panDeltaRef.current;

        panDeltaRef.current = fullInteractionDelta;

        onPanChange?.({ triggeredBy: 'pointer', delta: -currentDelta });
      }
    } else {
      const [start, end] = normalizeSelectionArea([
        startHoldingAt,
        currentPosition,
      ]);

      // If the selection area is outside the chart, we don't want to update it
      if (start.relative.x < 0 || end.relative.x > width) {
        return;
      }

      onSelectChange?.({
        triggeredBy: 'pointer',
        start,
        end,
        mode: activeMode,
      });
    }
  };

  const onDragEnd = (event: DragEndProps & PointerEventModifiers) => {
    const activeMode = getActiveMode(mode, event, isZoomXDisabled);

    if (activeMode === 'pan') {
      panDeltaRef.current = undefined;

      onPanEnd?.({ triggeredBy: 'pointer' });
    } else {
      const { startHoldingAt: startHoldingAtProp, releasedAt: releasedAtProp } =
        event;

      const startHoldingAt = centerPositionVertically(
        startHoldingAtProp,
        height,
      );
      const releasedAt = centerPositionVertically(releasedAtProp, height);

      const [start, end] = normalizeSelectionArea([startHoldingAt, releasedAt]);

      if (activeMode === 'zoom') {
        const deltaStart = start.relative.x;
        const deltaEnd = end.relative.x - width;

        onSelectEnd?.({ cancelled: true });
        onZoomChange?.({
          triggeredBy: 'pointer',
          deltaStart,
          deltaEnd,
          behavior: 'zoom-x',
        });
      } else {
        const normalizedSelectionArea = normalizeSelectionArea([
          startHoldingAt,
          releasedAt,
        ]);

        const { start, end } = capSelectionAtBoundaries(
          normalizedSelectionArea,
          width,
        );

        onSelectEnd?.({
          triggeredBy: 'pointer',
          start,
          end,
        });
      }

      lastMousePositionRef.current = releasedAt;
    }
  };

  const onDragStartWithMiddleButton = () => {
    onPanStart?.({ triggeredBy: 'pointer' });
    panDeltaRef.current = 0;
  };

  const onDragMoveWithMiddleButton = (
    event: DragMoveProps & PointerEventModifiers,
  ) => {
    if (!isUndefined(panDeltaRef.current)) {
      const { currentPosition, startHoldingAt } = event;

      const fullInteractionDelta =
        currentPosition.relative.x - startHoldingAt.relative.x;

      const currentDelta = fullInteractionDelta - panDeltaRef.current;

      panDeltaRef.current = fullInteractionDelta;

      onPanChange?.({ triggeredBy: 'pointer', delta: -currentDelta });
    }
  };

  const onDragEndWithMiddleButton = (
    event: DragEndProps & PointerEventModifiers,
  ) => {
    panDeltaRef.current = undefined;
    lastMousePositionRef.current = event.releasedAt;

    onPanEnd?.({ triggeredBy: 'pointer' });
  };

  const onMouseClickOrTap = (
    event: PointerEventPosition & PointerEventModifiers,
  ) => {
    const { position } = event;

    if (getActiveMode(mode, event) === 'explore') {
      if (isUndefined(selectionArea)) {
        onHover?.({
          triggeredBy: 'pointer',
          position,
        });

        onTogglePin?.({
          triggeredBy: 'pointer',
          pin: !pinned,
          position,
        });
      } else {
        onSelectEnd?.({ cancelled: true });

        onHover?.({
          triggeredBy: 'pointer',
          position,
        });
      }
    } else if (getActiveMode(mode, event, isZoomXDisabled) === 'zoom') {
      const { position } = event;
      const modifier = event.shiftKey ? -1 : 1;
      const ratio = position.relative.x / width;
      const delta = Math.floor(width * ZOOM_FACTOR) * modifier;

      const deltaStart = delta * ratio;
      const deltaEnd = -delta * (1 - ratio);

      onZoomChange?.({
        triggeredBy: 'pointer',
        deltaStart,
        deltaEnd,
        behavior: event.shiftKey ? 'zoom-out' : 'zoom-in',
      });
    }

    lastMousePositionRef.current = position;
  };

  const resetWithPositionBasedOnMode = (triggeredBy: InteractionTriggers) => {
    let mousePosition;
    if (mode === 'explore') {
      mousePosition = lastMousePositionRef.current;
    }
    onReset?.({
      triggeredBy,
      mousePosition,
    });

    reset();
  };

  const onDoubleClickOrTap = () => {
    resetWithPositionBasedOnMode('pointer');
  };

  const onMouseHover = (
    event: PointerEventPosition & PointerEventModifiers,
  ) => {
    const activeMode = getActiveMode(mode, event, isZoomXDisabled);

    const canExplore = !pinned && isUndefined(selectionArea);

    const { position } = event;

    if (
      (activeMode === 'explore' && canExplore) ||
      (activeMode !== 'explore' && !pinned)
    ) {
      onHover?.({ triggeredBy: 'pointer', position });
    }

    lastMousePositionRef.current = position;

    if (activeMode === 'zoom' && event.shiftKey) {
      if (cursorMode !== 'zoomOut') {
        setCursorMode('zoomOut');
      }
    } else if (activeMode !== cursorMode) {
      setCursorMode(activeMode);
    }
  };

  const onPinchStart = () => {
    reset();

    onZoomStart?.({ triggeredBy: 'pointer' });
  };

  const onPinchMove = (event: PinchMoveProps & PointerEventModifiers) => {
    const { delta } = event;

    const pinchIn = delta > 0;

    onZoomChange?.({
      triggeredBy: 'pointer',
      deltaStart: delta,
      deltaEnd: -delta,
      behavior: pinchIn ? 'zoom-in' : 'zoom-out',
    });
  };

  const onPinchEnd = () => {
    onZoomEnd?.({ triggeredBy: 'pointer' });
  };

  const onSwipeStart = () => {
    onPanStart?.({ triggeredBy: 'pointer' });
  };

  const onSwipeMove = (event: SwipeMoveProps & PointerEventModifiers) => {
    const { deltaX } = event;

    onPanChange?.({ triggeredBy: 'pointer', delta: -deltaX });
  };

  const onSwipeEnd = () => {
    onPanEnd?.({ triggeredBy: 'pointer' });
  };

  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = usePointerEvents({
    onDragStart,
    onDragMove,
    onDragEnd,
    onDragStartWithMiddleButton,
    onDragMoveWithMiddleButton,
    onDragEndWithMiddleButton,
    onTap: onMouseClickOrTap,
    onDoubleTap: onDoubleClickOrTap,
    onMouseClick: onMouseClickOrTap,
    onMouseDoubleClick: onDoubleClickOrTap,
    onMouseHover,
    onPinchStart,
    onPinchMove,
    onPinchEnd,
    onSwipeStart,
    onSwipeMove,
    onSwipeEnd,
    eventCancelled,
  });
  //#endregion

  //#region Native HTML events
  const handleMouseLeave = (
    event: ReactMouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    onLeave?.({ triggeredBy: 'pointer' });
    lastMousePositionRef.current = undefined;
  };

  const handleFocus = () => {
    onFocus?.();
  };

  const handleBlur = (event: FocusEvent) => {
    const nextFocusElement = event.relatedTarget;

    if (!event.target?.contains(nextFocusElement)) {
      onLeave?.({ triggeredBy: 'keyboard' });
    }
  };

  const handleWheel = (event: WheelEvent) => {
    if (
      getActiveMode(mode, event, isZoomDisabled) === 'zoom' &&
      isTemporaryZoomMode(event)
    ) {
      const vertical = event.deltaY && event.deltaY !== 0;
      const wheelPosition = calculateMousePositions(event, event.currentTarget);
      lastMousePositionRef.current = wheelPosition;

      if (vertical) {
        const wheelUp = event.deltaY < 0;

        const delta = wheelUp
          ? Math.min(event.deltaY, -MIN_ZOOM_STEP)
          : Math.max(event.deltaY, MIN_ZOOM_STEP);

        const ratio = wheelPosition.relative.x / width;
        const deltaStart = -delta * ratio;
        const deltaEnd = delta * (1 - ratio);

        onZoomChange?.({
          triggeredBy: 'pointer',
          deltaStart,
          deltaEnd,
          behavior: wheelUp ? 'zoom-in' : 'zoom-out',
        });
      }
    } else if (
      getActiveMode(mode, event) === 'pan' ||
      isTemporaryPanMode(event)
    ) {
      const horizontal = event.deltaX && event.deltaX !== 0;
      if (horizontal) {
        onPanChange?.({ triggeredBy: 'pointer', delta: event.deltaX });
      }
    }
  };
  //#endregion

  //#region Keyboard handlers
  const handleKeyDown = (event: KeyboardEvent) => {
    const activeMode = getActiveMode(mode, event, isZoomDisabled);

    switch (event.key) {
      case KeyboardControls.KEYBOARD_EXPLORE_MODE_SHORTCUT:
      case KeyboardControls.KEYBOARD_EXPLORE_MODE_SHORTCUT_ALTERNATIVE: {
        reset();

        if (!isUndefined(selectionArea)) {
          eventCancelled.current = true;
          onSelectEnd?.({ cancelled: true });
        }

        onModeChange?.({ mode: 'explore', keyboard: true });
        break;
      }
      case KeyboardControls.KEYBOARD_PAN_MODE_SHORTCUT:
      case KeyboardControls.KEYBOARD_PAN_MODE_SHORTCUT_ALTERNATIVE: {
        reset();

        onModeChange?.({ mode: 'pan', keyboard: true });
        break;
      }
      case KeyboardControls.KEYBOARD_ZOOM_MODE_SHORTCUT:
      case KeyboardControls.KEYBOARD_ZOOM_MODE_SHORTCUT_ALTERNATIVE: {
        reset();
        if (!isUndefined(selectionArea)) {
          eventCancelled.current = true;
          onSelectEnd?.({ cancelled: true });
        }

        onModeChange?.({ mode: 'zoom', keyboard: true });
        break;
      }
      case KeyboardControls.KEYBOARD_RESET_SHORTCUT:
      case KeyboardControls.KEYBOARD_RESET_SHORTCUT_ALTERNATIVE:
        resetWithPositionBasedOnMode('keyboard');
        break;
      case KeyboardControls.UP_ARROW: {
        if (activeMode === 'zoom' && isTemporaryZoomMode(event)) {
          event.preventDefault();
          const delta = Math.floor((width * ZOOM_FACTOR) / 2);

          onZoomChange?.({
            triggeredBy: 'keyboard',
            deltaStart: delta,
            deltaEnd: -delta,
            behavior: 'zoom-in',
          });
        }

        break;
      }
      case KeyboardControls.DOWN_ARROW: {
        if (activeMode === 'zoom' && isTemporaryZoomMode(event)) {
          event.preventDefault();
          const delta = Math.floor((width * ZOOM_FACTOR) / 2);

          onZoomChange?.({
            triggeredBy: 'keyboard',
            deltaStart: -delta,
            deltaEnd: delta,
            behavior: 'zoom-out',
          });
        }

        break;
      }
      case KeyboardControls.LEFT_ARROW: {
        if (activeMode === 'pan') {
          const factor = event.shiftKey
            ? -LARGE_STEP_FACTOR
            : -SMALL_STEP_FACTOR;
          const delta = width * factor;

          onPanChange?.({ triggeredBy: 'keyboard', delta });
        }

        break;
      }
      case KeyboardControls.RIGHT_ARROW: {
        if (activeMode === 'pan') {
          const factor = event.shiftKey ? LARGE_STEP_FACTOR : SMALL_STEP_FACTOR;
          const delta = width * factor;

          onPanChange?.({ triggeredBy: 'keyboard', delta });
        }

        break;
      }
      case KeyboardControls.ENTER: {
        if (!isUndefined(selectionArea)) {
          if (
            (activeMode === 'explore' && !isZoomXDisabled) ||
            activeMode === 'zoom'
          ) {
            const deltaStart = selectionArea[0].relative.x;
            const deltaEnd = selectionArea[1].relative.x - width;

            onSelectEnd?.({ cancelled: true });
            onZoomChange?.({
              triggeredBy: 'keyboard',
              deltaStart,
              deltaEnd,
              behavior: 'zoom-x',
            });

            if (lastMousePositionRef.current) {
              onHover?.({
                triggeredBy: 'pointer',
                position: lastMousePositionRef.current,
              });
            }
          }
        }
        break;
      }
      case KeyboardControls.ESCAPE: {
        if (activeMode === 'pan') {
          onPanEnd?.({ triggeredBy: 'keyboard' });
        }

        if (!isUndefined(selectionArea)) {
          eventCancelled.current = true;
          onSelectEnd?.({ cancelled: true });

          if (lastMousePositionRef.current) {
            onHover?.({
              triggeredBy: 'pointer',
              position: lastMousePositionRef.current,
            });
          }
        }

        if (pinned) {
          onTogglePin?.({
            triggeredBy: 'keyboard',
            pin: false,
            position: lastMousePositionRef.current,
          });
        }

        if (mode !== 'explore') {
          onModeChange?.({ mode: 'explore', keyboard: false });
        }

        reset();

        break;
      }
    }
  };
  //#endregion

  return (
    <div
      ref={rootRef}
      role={'presentation'}
      tabIndex={0}
      className={classNames(
        chartEventsWrapper({ mode: cursorMode }),
        className,
      )}
      style={style}
      onMouseDown={(event) => {
        if (isOwnEvent(event)) {
          handleMouseDown(event);
        }
      }}
      onMouseMove={(event) => {
        if (isOwnEvent(event)) {
          handleMouseMove(event);
        }
      }}
      onMouseUp={(event) => {
        if (isOwnEvent(event)) {
          handleMouseUp(event);
        }
      }}
      onMouseLeave={handleMouseLeave}
      onTouchStart={(event) => {
        if (isOwnEvent(event)) {
          handleTouchStart(event);
        }
      }}
      onTouchMove={(event) => {
        if (isOwnEvent(event)) {
          handleTouchMove(event);
        }
      }}
      onTouchEnd={(event) => {
        if (isOwnEvent(event)) {
          handleTouchEnd(event);
        }
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      {...restProps}
    >
      {children}
    </div>
  );
});

(InteractionsOverlay as typeof InteractionsOverlay & { displayName: string })[
  'displayName'
] = 'InteractionsOverlay';
